// ═══════════════════════════════════════════════════════════════════
//  MinimapHeader — Breadcrumb above the minimap canvas.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase E of MINIMAP_ATLAS_PLAN. Shows: Atlas › Category › Doc › Sheet.
//  Each segment is clickable. Hidden when no atlas context is provided
//  so single-workflow users don't get extra chrome.
//
// ═══════════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MinimapBreadcrumbSegment } from '@/types/minimap';

export interface MinimapHeaderProps {
  segments: MinimapBreadcrumbSegment[];
  /** When true, render the "back to atlas" pill on the right. */
  showBackToAtlas?: boolean;
  className?: string;
}

export function MinimapHeader({ segments, showBackToAtlas = false, className }: MinimapHeaderProps) {
  const navigate = useNavigate();
  if (segments.length === 0) return null;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-1 px-1 py-0.5 text-[10px] text-muted-foreground',
        className,
      )}
      data-fw-minimap-breadcrumb="true"
    >
      <ol className="flex min-w-0 items-center gap-0.5">
        {segments.map((seg, i) => (
          <li key={`${seg.kind}-${i}`} className="flex items-center gap-0.5 min-w-0">
            {i > 0 && <ChevronRight size={10} className="shrink-0 opacity-60" />}
            <button
              type="button"
              onClick={() => navigate(seg.href)}
              className={cn(
                'min-w-0 truncate rounded px-1 py-0.5 transition-colors hover:bg-accent hover:text-accent-foreground',
                i === segments.length - 1 && 'font-medium text-foreground',
              )}
              data-fw-minimap-crumb={seg.kind}
              title={seg.label}
            >
              {seg.label}
            </button>
          </li>
        ))}
      </ol>
      {showBackToAtlas && (
        <button
          type="button"
          onClick={() => navigate('/atlas')}
          className="flex shrink-0 items-center gap-0.5 rounded border bg-background px-1 py-0.5 text-[9px] hover:bg-accent"
          aria-label="Back to atlas"
        >
          <ArrowLeft size={9} />
          atlas
        </button>
      )}
    </div>
  );
}
